import _ from 'lodash';
import { all, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { actionTypes, actions } from './modules';
import { apiServiceAsync } from '../services/apiService';
import {
  recruitmentProfilePostBodySelector,
  newProfileRequestBodySelector,
  profileIdSelector,
  activeStepSelector
} from './selectors';
import history from '../../../../../shared/frontend/libs/history';
import { namedRoutes, resolveRoutePath } from '../bundles/routes';
import { RecruitmentConfigSteps } from '../../../../../shared/frontend/scripts/recruitment/constants';
import { serializeRecruitmentProfile } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { adjustScheduleArrayForTimezone } from '../../../../../shared/common/recruitment';
import { offsetToTimezone } from '../../../../../shared/frontend/libs/utils';
import { actions as recruitmentAction } from '../shared/recruitment/modules';
import { ownProfilesSelector } from '../shared/recruitment/selectors';

const { LOAD_PROFILE, CHANGE_STEP, SAVE_PROFILE, UPDATE_PROFILE_FIELD } = actionTypes;

const {
  loadProfileFail,
  setEditProfile,
  saveProfileFail,
  changeStep,
  requestProfileCreate,
  createRecruitmentProfileSuccess,
  loadStaticProfileDataSuccess,
  loadStaticProfileDataFail,
  createProfileFail,
  loadUserProfileSuccess,
  loadUserProfileFail
} = actions;

function* requestSingleProfile(action: any): any {
  const { activeStep } = action.payload;
  const profileId = yield select(profileIdSelector);

  // if profileId is -1, the reducer will set the correct loading states
  // note: I think the -1 for create was removed when the "create" route was specifically added,
  // but keeping this check for now and will confirm later.
  if (profileId > 0) {
    try {
      const { profileData } = yield call(apiServiceAsync.getRecruitmentProfile, profileId);
      if (activeStep) {
        yield put(changeStep(activeStep));
      }

      // this is called when loading a profile from server where the schedule times
      // are stored in UTC. So we convert to the timezone of the actual timezone,
      // so that the numbers displayed in the editor UI will match what they were set
      // to when the user saved
      if (profileData.schedule?.days) {
        profileData.schedule.days = adjustScheduleArrayForTimezone(
          profileData.schedule.days,
          offsetToTimezone(profileData.profile.schedule_timezone)
        );
      }

      yield put(setEditProfile(profileData));
    } catch (e) {
      yield put(loadProfileFail());
    }
  }
}

function* createRecruitmentProfileSaga(action: any): any {
  const { payload } = action;
  if (payload.fieldPath === 'activity_type') {
    yield put(requestProfileCreate());
    const body = yield select(newProfileRequestBodySelector);
    try {
      const { recruitmentProfile } = yield call(apiServiceAsync.createRecruitmentProfile, body);
      history.push(resolveRoutePath(namedRoutes.RecruitmentProfileConfig, { profile_id: recruitmentProfile.id }));
      yield put(createRecruitmentProfileSuccess(recruitmentProfile));
    } catch (e: any) {
      yield put(createProfileFail(e));
    }
  }
}
function* saveRecruitmentProfileSaga(action: any): any {
  const { saveType } = action.payload.saveStatus;

  const profile = yield select(recruitmentProfilePostBodySelector);
  if (saveType === 'activate') {
    profile.activationStatus = 'active';
  }

  try {
    const res = yield call(apiServiceAsync.saveCharacterRecruitmentProfile, profile);
    // update state to display current profiles
    const ownProfiles = yield select(ownProfilesSelector);
    const updatedProfiles = ownProfiles.map((profileItem: any) => {
      if (profileItem.id === res.profileData.profile.id) {
        const updatedProfile = {
          ...res.profileData
        };
        return serializeRecruitmentProfile({}, updatedProfile);
      }
      return profileItem;
    });

    yield put(recruitmentAction.updateOwnProfiles(updatedProfiles || []));

    // TODO: support an option that lets us stay in the editor?

    // return to list view and reset state
    yield put(changeStep(''));
    history.push(resolveRoutePath(namedRoutes.UserRecruitmentProfiles));
  } catch (e) {
    yield put(saveProfileFail());
  }
}

function* loadStaticProfileDataSaga(): any {
  const profileId = yield select(profileIdSelector);
  try {
    // tags + requirements
    const staticData = yield call(apiServiceAsync.getStaticRecruitmenProfileData, profileId);
    yield put(loadStaticProfileDataSuccess(staticData));
  } catch (e) {
    // need to ensure this shows error states or proper messaging - preference, @jah?
    yield put(loadStaticProfileDataFail());
  }
}

function* loadUserSettingsProfileSaga(): any {
  try {
    const userProfile = yield call(apiServiceAsync.getUserProfileSettings);
    yield put(loadUserProfileSuccess(userProfile));
  } catch (e) {
    yield put(loadUserProfileFail());
  }
}

function* syncRoute(action: any): any {
  const { nextStep } = action.payload;
  const profileId = yield select(profileIdSelector);

  if (nextStep && profileId !== -1) {
    history.push(
      resolveRoutePath(namedRoutes.RecruitmentProfileConfig, {
        step: nextStep,
        profile_id: profileId
      })
    );
  }
}

const stepSpecificSagas: { [key: string]: any[] } = {
  [RecruitmentConfigSteps.CharacterSlots]: [],
  [RecruitmentConfigSteps.PlayerSlots]: [],
  [RecruitmentConfigSteps.Schedule]: [],
  [RecruitmentConfigSteps.Requirements]: [loadStaticProfileDataSaga],
  [RecruitmentConfigSteps.Publish]: [loadUserSettingsProfileSaga, loadStaticProfileDataSaga]
};
function* fetchStepSpecificDataSaga(): any {
  const activeStep = yield select(activeStepSelector);
  yield all(_.map(stepSpecificSagas[activeStep], (s) => s()));
}

// observers:
function* watchSaveProfile(): any {
  yield takeEvery(SAVE_PROFILE, saveRecruitmentProfileSaga);
}

function* watchSelectActivity(): any {
  // there is an explicit action for creating, could use that instead of wathcing
  // every profile update. May be able to use this however to trigger saves

  yield takeLatest(UPDATE_PROFILE_FIELD, createRecruitmentProfileSaga);
}

function* watchRequestSingleProfile(): any {
  yield takeEvery(LOAD_PROFILE, requestSingleProfile);
}

// this will need to be per change step as well as direct clicks from the profile
// navigation items
function* watchChangeStep(): any {
  yield takeLatest(CHANGE_STEP, fetchStepSpecificDataSaga);
  yield takeLatest(CHANGE_STEP, syncRoute);
}

export default [watchSaveProfile(), watchRequestSingleProfile(), watchSelectActivity(), watchChangeStep()];
